/**
 * ETL Driver Registry — defines every supported connection driver.
 *
 * Each driver entry provides:
 * - connectionFields: the form fields shown in the Connection Manager
 * - advancedFields: optional fields in a collapsible section
 * - versions: driver versions with JAR info and download URLs
 * - testQuery: database-specific test query
 * - jdbcUrlTemplate: template for generating JDBC URLs
 */

// ---------------------------------------------------------------------------
// Driver Registry
// ---------------------------------------------------------------------------

export const DRIVER_REGISTRY = {

  // ─── Relational Databases ───────────────────────────────────

  postgresql: {
    id: 'postgresql',
    name: 'PostgreSQL',
    category: 'Relational',
    icon: 'Database',
    versions: [
      { id: 'pg-42', label: '42.x (Current)', jarName: 'postgresql-42.7.4.jar',
        downloadUrl: 'https://jdbc.postgresql.org/download/' },
      { id: 'pg-9', label: '9.x (Legacy)', jarName: 'postgresql-9.4.1212.jar',
        downloadUrl: 'https://jdbc.postgresql.org/download/' },
    ],
    defaultVersion: 'pg-42',
    defaultPort: 5432,
    jdbcUrlTemplate: 'jdbc:postgresql://{host}:{port}/{database}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 5432, required: true },
      { key: 'database', label: 'Database', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
      { key: 'schema', label: 'Schema', type: 'text', default: 'public' },
    ],
    advancedFields: [
      { key: 'ssl', label: 'Use SSL', type: 'checkbox', default: false },
      { key: 'sslMode', label: 'SSL Mode', type: 'select',
        options: ['disable', 'allow', 'prefer', 'require', 'verify-ca', 'verify-full'],
        default: 'prefer', visibleWhen: { ssl: true } },
      { key: 'connectionTimeout', label: 'Connection Timeout (s)', type: 'number', default: 30 },
      { key: 'applicationName', label: 'Application Name', type: 'text', default: 'RecodeIT' },
    ],
    testQuery: 'SELECT 1',
  },

  mysql: {
    id: 'mysql',
    name: 'MySQL',
    category: 'Relational',
    icon: 'Database',
    versions: [
      { id: 'mysql-8', label: '8.x Connector/J', jarName: 'mysql-connector-j-8.4.0.jar',
        downloadUrl: 'https://dev.mysql.com/downloads/connector/j/' },
      { id: 'mysql-5', label: '5.x Connector/J', jarName: 'mysql-connector-java-5.1.49.jar',
        downloadUrl: 'https://dev.mysql.com/downloads/connector/j/' },
    ],
    defaultVersion: 'mysql-8',
    defaultPort: 3306,
    jdbcUrlTemplate: 'jdbc:mysql://{host}:{port}/{database}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 3306, required: true },
      { key: 'database', label: 'Database', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
    ],
    advancedFields: [
      { key: 'ssl', label: 'Use SSL', type: 'checkbox', default: false },
      { key: 'connectionTimeout', label: 'Connection Timeout (s)', type: 'number', default: 30 },
      { key: 'allowPublicKeyRetrieval', label: 'Allow Public Key Retrieval', type: 'checkbox', default: false },
    ],
    testQuery: 'SELECT 1',
  },

  sqlserver: {
    id: 'sqlserver',
    name: 'SQL Server',
    category: 'Relational',
    icon: 'Database',
    versions: [
      { id: 'mssql-12', label: 'JDBC 12.x (Current)', jarName: 'mssql-jdbc-12.8.1.jre11.jar',
        downloadUrl: 'https://learn.microsoft.com/en-us/sql/connect/jdbc/download-microsoft-jdbc-driver-for-sql-server' },
      { id: 'mssql-9', label: 'JDBC 9.x', jarName: 'mssql-jdbc-9.4.1.jre11.jar',
        downloadUrl: 'https://learn.microsoft.com/en-us/sql/connect/jdbc/download-microsoft-jdbc-driver-for-sql-server' },
      { id: 'jtds', label: 'jTDS 1.3.x (Legacy)', jarName: 'jtds-1.3.1.jar',
        downloadUrl: 'https://sourceforge.net/projects/jtds/' },
    ],
    defaultVersion: 'mssql-12',
    defaultPort: 1433,
    jdbcUrlTemplate: 'jdbc:sqlserver://{host}:{port};databaseName={database}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 1433, required: true },
      { key: 'database', label: 'Database', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
      { key: 'schema', label: 'Schema', type: 'text', default: 'dbo' },
    ],
    advancedFields: [
      { key: 'encrypt', label: 'Encrypt', type: 'select',
        options: ['true', 'false', 'strict'], default: 'true' },
      { key: 'trustServerCertificate', label: 'Trust Server Certificate', type: 'checkbox', default: true },
      { key: 'integratedSecurity', label: 'Windows Auth', type: 'checkbox', default: false },
      { key: 'connectionTimeout', label: 'Connection Timeout (s)', type: 'number', default: 30 },
      { key: 'instanceName', label: 'Named Instance', type: 'text', default: '' },
    ],
    testQuery: 'SELECT 1',
  },

  oracle: {
    id: 'oracle',
    name: 'Oracle',
    category: 'Relational',
    icon: 'Database',
    versions: [
      { id: 'ojdbc-23', label: 'JDBC 23c', jarName: 'ojdbc11-23.5.0.24.07.jar',
        downloadUrl: 'https://www.oracle.com/database/technologies/appdev/jdbc-downloads.html' },
      { id: 'ojdbc-19', label: 'JDBC 19c', jarName: 'ojdbc10-19.24.0.0.jar',
        downloadUrl: 'https://www.oracle.com/database/technologies/appdev/jdbc-downloads.html' },
      { id: 'ojdbc-12', label: 'JDBC 12c', jarName: 'ojdbc8-12.2.0.1.jar',
        downloadUrl: 'https://www.oracle.com/database/technologies/appdev/jdbc-downloads.html' },
      { id: 'ojdbc-11', label: 'JDBC 11g', jarName: 'ojdbc6-11.2.0.4.jar',
        downloadUrl: 'https://www.oracle.com/database/technologies/appdev/jdbc-downloads.html' },
    ],
    defaultVersion: 'ojdbc-19',
    defaultPort: 1521,
    jdbcUrlTemplate: 'jdbc:oracle:thin:@//{host}:{port}/{service}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 1521, required: true },
      { key: 'connectionMode', label: 'Connection Mode', type: 'select',
        options: [
          { value: 'service', label: 'Service Name' },
          { value: 'sid', label: 'SID' },
          { value: 'tns', label: 'TNS Name' },
        ], default: 'service', required: true },
      { key: 'service', label: 'Service Name', type: 'text', default: '', required: true,
        visibleWhen: { connectionMode: 'service' } },
      { key: 'sid', label: 'SID', type: 'text', default: '', required: true,
        visibleWhen: { connectionMode: 'sid' } },
      { key: 'tnsName', label: 'TNS Name', type: 'text', default: '', required: true,
        visibleWhen: { connectionMode: 'tns' } },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
      { key: 'schema', label: 'Schema', type: 'text', default: '' },
    ],
    advancedFields: [
      { key: 'connectionTimeout', label: 'Connection Timeout (s)', type: 'number', default: 30 },
      { key: 'fetchSize', label: 'Default Fetch Size', type: 'number', default: 1000 },
    ],
    testQuery: 'SELECT 1 FROM DUAL',
  },

  db2: {
    id: 'db2',
    name: 'IBM DB2',
    category: 'Relational',
    icon: 'Database',
    versions: [
      { id: 'db2-12', label: 'JDBC 12.x / 11.5+', jarName: 'db2jcc4.jar',
        downloadUrl: 'https://www.ibm.com/support/pages/db2-jdbc-driver-versions-and-downloads',
        notes: 'JCC Type 4 driver. Works with DB2 11.5 and 12.1.' },
      { id: 'db2-10', label: 'JDBC 10.x / 9.x', jarName: 'db2jcc.jar',
        downloadUrl: 'https://www.ibm.com/support/pages/db2-jdbc-driver-versions-and-downloads',
        notes: 'Legacy JCC driver for DB2 9.x and 10.x.' },
      { id: 'db2-jtopen', label: 'JTOpen (IBM i / AS/400)', jarName: 'jt400.jar',
        downloadUrl: 'https://sourceforge.net/projects/jt400/',
        notes: 'For DB2 on IBM i (AS/400). Different JDBC URL format.' },
    ],
    defaultVersion: 'db2-12',
    defaultPort: 50000,
    jdbcUrlTemplate: 'jdbc:db2://{host}:{port}/{database}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 50000, required: true },
      { key: 'database', label: 'Database', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
      { key: 'schema', label: 'Schema', type: 'text', default: '' },
    ],
    advancedFields: [
      { key: 'currentSchema', label: 'Current Schema (SET SCHEMA)', type: 'text', default: '' },
      { key: 'securityMechanism', label: 'Security Mechanism', type: 'select',
        options: [
          { value: '3', label: 'Clear Text (3)' },
          { value: '4', label: 'User ID Only (4)' },
          { value: '7', label: 'Encrypted User/Password (7)' },
          { value: '9', label: 'Encrypted + Security Token (9)' },
        ], default: '3' },
      { key: 'sslConnection', label: 'Use SSL', type: 'checkbox', default: false },
      { key: 'connectionTimeout', label: 'Connection Timeout (s)', type: 'number', default: 30 },
    ],
    testQuery: 'SELECT 1 FROM SYSIBM.SYSDUMMY1',
  },

  // ─── Mainframe ──────────────────────────────────────────────

  idms: {
    id: 'idms',
    name: 'CA-IDMS',
    category: 'Mainframe',
    icon: 'Server',
    versions: [
      { id: 'idms-19', label: 'IDMS 19.0+', jarName: 'idmsjdbc.jar',
        downloadUrl: null,
        notes: 'Broadcom proprietary. Customer must provide JAR.' },
    ],
    defaultVersion: 'idms-19',
    defaultPort: 3709,
    jdbcUrlTemplate: 'jdbc:ca-idms://{host}:{port}/{dictionary}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: '', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 3709, required: true },
      { key: 'dictionary', label: 'Dictionary Name', type: 'text', default: '', required: true,
        description: 'IDMS data dictionary name' },
      { key: 'subschema', label: 'Subschema', type: 'text', default: '',
        description: 'IDMS subschema name' },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
    ],
    advancedFields: [
      { key: 'taskCode', label: 'Task Code', type: 'text', default: '' },
      { key: 'nodeName', label: 'Node Name', type: 'text', default: '' },
    ],
    testQuery: 'SELECT 1',
  },

  ims: {
    id: 'ims',
    name: 'IBM IMS',
    category: 'Mainframe',
    icon: 'Server',
    versions: [
      { id: 'ims-15', label: 'IMS 15.x Universal', jarName: 'imsjava.jar',
        downloadUrl: null,
        notes: 'IBM proprietary. Customer provides from IMS Connect.' },
    ],
    defaultVersion: 'ims-15',
    defaultPort: 9999,
    jdbcUrlTemplate: 'jdbc:ims://{host}:{port}/{datastoreName}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: '', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 9999, required: true },
      { key: 'datastoreName', label: 'Datastore Name', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
    ],
    advancedFields: [
      { key: 'driverType', label: 'Driver Type', type: 'select',
        options: ['4', '2'], default: '4' },
    ],
    testQuery: 'SELECT 1',
  },

  // ─── Cloud Data Warehouses ──────────────────────────────────

  snowflake: {
    id: 'snowflake',
    name: 'Snowflake',
    category: 'Cloud',
    icon: 'Cloud',
    versions: [
      { id: 'sf-3', label: 'JDBC 3.x (Current)', jarName: 'snowflake-jdbc-3.19.0.jar',
        downloadUrl: 'https://docs.snowflake.com/en/developer-guide/jdbc/jdbc-download' },
    ],
    defaultVersion: 'sf-3',
    defaultPort: 443,
    jdbcUrlTemplate: 'jdbc:snowflake://{account}.snowflakecomputing.com/',
    connectionFields: [
      { key: 'account', label: 'Account Identifier', type: 'text', default: '', required: true,
        description: 'e.g., xy12345.us-east-1' },
      { key: 'warehouse', label: 'Warehouse', type: 'text', default: '', required: true },
      { key: 'database', label: 'Database', type: 'text', default: '', required: true },
      { key: 'schema', label: 'Schema', type: 'text', default: 'PUBLIC' },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
      { key: 'role', label: 'Role', type: 'text', default: '' },
    ],
    advancedFields: [
      { key: 'authenticator', label: 'Authenticator', type: 'select',
        options: ['snowflake', 'externalbrowser', 'oauth'], default: 'snowflake' },
    ],
    testQuery: 'SELECT 1',
  },

  redshift: {
    id: 'redshift',
    name: 'Amazon Redshift',
    category: 'Cloud',
    icon: 'Cloud',
    versions: [
      { id: 'rs-2', label: 'JDBC 2.x (Current)', jarName: 'redshift-jdbc42-2.1.0.31.jar',
        downloadUrl: 'https://docs.aws.amazon.com/redshift/latest/mgmt/configure-jdbc-connection.html' },
    ],
    defaultVersion: 'rs-2',
    defaultPort: 5439,
    jdbcUrlTemplate: 'jdbc:redshift://{host}:{port}/{database}',
    connectionFields: [
      { key: 'host', label: 'Cluster Endpoint', type: 'text', default: '', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 5439, required: true },
      { key: 'database', label: 'Database', type: 'text', default: 'dev', required: true },
      { key: 'username', label: 'User', type: 'text', default: '', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
      { key: 'schema', label: 'Schema', type: 'text', default: 'public' },
    ],
    advancedFields: [
      { key: 'ssl', label: 'Use SSL', type: 'checkbox', default: true },
    ],
    testQuery: 'SELECT 1',
  },

  bigquery: {
    id: 'bigquery',
    name: 'Google BigQuery',
    category: 'Cloud',
    icon: 'Cloud',
    versions: [
      { id: 'bq-simba', label: 'Simba JDBC', jarName: 'GoogleBigQueryJDBC42.jar',
        downloadUrl: 'https://cloud.google.com/bigquery/docs/reference/odbc-jdbc-drivers' },
    ],
    defaultVersion: 'bq-simba',
    defaultPort: 443,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'project', label: 'Project ID', type: 'text', default: '', required: true },
      { key: 'dataset', label: 'Default Dataset', type: 'text', default: '' },
      { key: 'authMode', label: 'Authentication', type: 'select',
        options: [
          { value: 'service_account', label: 'Service Account JSON' },
          { value: 'oauth', label: 'OAuth 2.0' },
        ], default: 'service_account' },
      { key: 'keyFilePath', label: 'Service Account Key File', type: 'text', default: '',
        visibleWhen: { authMode: 'service_account' } },
    ],
    advancedFields: [],
    testQuery: 'SELECT 1',
  },

  // ─── NoSQL ──────────────────────────────────────────────────

  mongodb: {
    id: 'mongodb',
    name: 'MongoDB',
    category: 'NoSQL',
    icon: 'Database',
    versions: [
      { id: 'mongo-5', label: 'Driver 5.x', jarName: 'mongodb-driver-sync-5.2.0.jar',
        downloadUrl: 'https://www.mongodb.com/docs/drivers/java-drivers/' },
      { id: 'mongo-4', label: 'Driver 4.x', jarName: 'mongodb-driver-sync-4.11.4.jar',
        downloadUrl: 'https://www.mongodb.com/docs/drivers/java-drivers/' },
    ],
    defaultVersion: 'mongo-5',
    defaultPort: 27017,
    jdbcUrlTemplate: 'mongodb://{host}:{port}/{database}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 27017, required: true },
      { key: 'database', label: 'Database', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: '' },
      { key: 'password', label: 'Password', type: 'password', default: '' },
      { key: 'authDatabase', label: 'Auth Database', type: 'text', default: 'admin' },
    ],
    advancedFields: [
      { key: 'connectionString', label: 'Connection String (overrides above)', type: 'text', default: '',
        description: 'Full MongoDB URI. If set, host/port/database are ignored.' },
      { key: 'ssl', label: 'Use SSL/TLS', type: 'checkbox', default: false },
      { key: 'replicaSet', label: 'Replica Set', type: 'text', default: '' },
    ],
    testQuery: null,
  },

  neo4j: {
    id: 'neo4j',
    name: 'Neo4j',
    category: 'NoSQL',
    icon: 'GitBranch',
    versions: [
      { id: 'neo4j-5', label: 'Driver 5.x (Neo4j 5)', jarName: 'neo4j-java-driver-5.25.0.jar',
        downloadUrl: 'https://neo4j.com/docs/java-manual/current/' },
      { id: 'neo4j-4', label: 'Driver 4.x (Neo4j 4)', jarName: 'neo4j-java-driver-4.4.13.jar',
        downloadUrl: 'https://neo4j.com/docs/java-manual/current/' },
    ],
    defaultVersion: 'neo4j-5',
    defaultPort: 7687,
    jdbcUrlTemplate: 'bolt://{host}:{port}',
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 7687, required: true },
      { key: 'database', label: 'Database', type: 'text', default: 'neo4j' },
      { key: 'username', label: 'User', type: 'text', default: 'neo4j', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '', required: true },
    ],
    advancedFields: [
      { key: 'protocol', label: 'Protocol', type: 'select',
        options: ['bolt', 'bolt+s', 'bolt+ssc', 'neo4j', 'neo4j+s', 'neo4j+ssc'],
        default: 'bolt' },
    ],
    testQuery: 'RETURN 1',
  },

  // ─── Object Storage ─────────────────────────────────────────

  s3: {
    id: 's3',
    name: 'Amazon S3',
    category: 'Storage',
    icon: 'HardDrive',
    versions: [
      { id: 's3-v2', label: 'AWS SDK v2', jarName: null, downloadUrl: null },
    ],
    defaultVersion: 's3-v2',
    defaultPort: null,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'bucket', label: 'Bucket', type: 'text', default: '', required: true },
      { key: 'region', label: 'Region', type: 'select',
        options: ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'eu-west-1', 'eu-central-1',
                  'ap-southeast-1', 'ap-northeast-1'],
        default: 'us-east-1', required: true },
      { key: 'accessKeyId', label: 'Access Key ID', type: 'text', default: '' },
      { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password', default: '' },
      { key: 'prefix', label: 'Key Prefix', type: 'text', default: '' },
    ],
    advancedFields: [
      { key: 'endpoint', label: 'Custom Endpoint', type: 'text', default: '',
        description: 'For S3-compatible services (MinIO, etc.)' },
      { key: 'pathStyleAccess', label: 'Path-Style Access', type: 'checkbox', default: false },
    ],
    testQuery: null,
  },

  'azure-blob': {
    id: 'azure-blob',
    name: 'Azure Blob Storage',
    category: 'Storage',
    icon: 'HardDrive',
    versions: [
      { id: 'az-12', label: 'Azure SDK 12.x', jarName: null, downloadUrl: null },
    ],
    defaultVersion: 'az-12',
    defaultPort: null,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'accountName', label: 'Storage Account', type: 'text', default: '', required: true },
      { key: 'containerName', label: 'Container', type: 'text', default: '', required: true },
      { key: 'accountKey', label: 'Account Key', type: 'password', default: '' },
      { key: 'sasToken', label: 'SAS Token', type: 'password', default: '' },
    ],
    advancedFields: [],
    testQuery: null,
  },

  gcs: {
    id: 'gcs',
    name: 'Google Cloud Storage',
    category: 'Storage',
    icon: 'HardDrive',
    versions: [
      { id: 'gcs-2', label: 'GCS Client 2.x', jarName: null, downloadUrl: null },
    ],
    defaultVersion: 'gcs-2',
    defaultPort: null,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'project', label: 'Project ID', type: 'text', default: '', required: true },
      { key: 'bucket', label: 'Bucket', type: 'text', default: '', required: true },
      { key: 'keyFilePath', label: 'Service Account Key File', type: 'text', default: '' },
    ],
    advancedFields: [],
    testQuery: null,
  },

  // ─── Streaming ──────────────────────────────────────────────

  kafka: {
    id: 'kafka',
    name: 'Apache Kafka',
    category: 'Streaming',
    icon: 'Radio',
    versions: [
      { id: 'kafka-3', label: 'Client 3.x', jarName: 'kafka-clients-3.8.0.jar',
        downloadUrl: 'https://kafka.apache.org/downloads' },
    ],
    defaultVersion: 'kafka-3',
    defaultPort: 9092,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'bootstrapServers', label: 'Bootstrap Servers', type: 'text',
        default: 'localhost:9092', required: true,
        description: 'Comma-separated host:port pairs' },
      { key: 'groupId', label: 'Consumer Group ID', type: 'text', default: 'modernizeit-etl' },
    ],
    advancedFields: [
      { key: 'securityProtocol', label: 'Security Protocol', type: 'select',
        options: ['PLAINTEXT', 'SSL', 'SASL_PLAINTEXT', 'SASL_SSL'], default: 'PLAINTEXT' },
      { key: 'saslMechanism', label: 'SASL Mechanism', type: 'select',
        options: ['PLAIN', 'SCRAM-SHA-256', 'SCRAM-SHA-512', 'GSSAPI'],
        default: 'PLAIN', visibleWhen: { securityProtocol: ['SASL_PLAINTEXT', 'SASL_SSL'] } },
      { key: 'saslUsername', label: 'SASL Username', type: 'text', default: '',
        visibleWhen: { securityProtocol: ['SASL_PLAINTEXT', 'SASL_SSL'] } },
      { key: 'saslPassword', label: 'SASL Password', type: 'password', default: '',
        visibleWhen: { securityProtocol: ['SASL_PLAINTEXT', 'SASL_SSL'] } },
      { key: 'schemaRegistryUrl', label: 'Schema Registry URL', type: 'text', default: '' },
    ],
    testQuery: null,
  },

  // ─── File / HDFS ────────────────────────────────────────────

  hdfs: {
    id: 'hdfs',
    name: 'HDFS',
    category: 'Storage',
    icon: 'HardDrive',
    versions: [
      { id: 'hdfs-3', label: 'Hadoop 3.x', jarName: null, downloadUrl: null },
    ],
    defaultVersion: 'hdfs-3',
    defaultPort: 9000,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'nameNode', label: 'NameNode URL', type: 'text', default: 'hdfs://localhost:9000', required: true },
      { key: 'username', label: 'User', type: 'text', default: '' },
    ],
    advancedFields: [
      { key: 'kerberosEnabled', label: 'Kerberos Auth', type: 'checkbox', default: false },
      { key: 'kerberosPrincipal', label: 'Principal', type: 'text', default: '',
        visibleWhen: { kerberosEnabled: true } },
    ],
    testQuery: null,
  },

  // ─── Other Databases ────────────────────────────────────────

  cassandra: {
    id: 'cassandra',
    name: 'Cassandra',
    category: 'NoSQL',
    icon: 'Database',
    versions: [
      { id: 'cass-4', label: 'Driver 4.x', jarName: 'java-driver-core-4.18.1.jar',
        downloadUrl: 'https://docs.datastax.com/en/driver-matrix/docs/java-drivers.html' },
    ],
    defaultVersion: 'cass-4',
    defaultPort: 9042,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'contactPoints', label: 'Contact Points', type: 'text', default: 'localhost', required: true,
        description: 'Comma-separated host:port pairs' },
      { key: 'port', label: 'Port', type: 'number', default: 9042, required: true },
      { key: 'keyspace', label: 'Keyspace', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: '' },
      { key: 'password', label: 'Password', type: 'password', default: '' },
    ],
    advancedFields: [
      { key: 'datacenter', label: 'Local Datacenter', type: 'text', default: 'datacenter1' },
    ],
    testQuery: null,
  },

  elasticsearch: {
    id: 'elasticsearch',
    name: 'Elasticsearch',
    category: 'NoSQL',
    icon: 'Search',
    versions: [
      { id: 'es-8', label: 'Client 8.x', jarName: 'elasticsearch-java-8.16.0.jar',
        downloadUrl: 'https://www.elastic.co/guide/en/elasticsearch/client/java-api-client/current/' },
    ],
    defaultVersion: 'es-8',
    defaultPort: 9200,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 9200, required: true },
      { key: 'index', label: 'Index', type: 'text', default: '' },
      { key: 'username', label: 'User', type: 'text', default: '' },
      { key: 'password', label: 'Password', type: 'password', default: '' },
    ],
    advancedFields: [
      { key: 'ssl', label: 'Use SSL', type: 'checkbox', default: false },
      { key: 'apiKey', label: 'API Key', type: 'password', default: '' },
    ],
    testQuery: null,
  },

  redis: {
    id: 'redis',
    name: 'Redis',
    category: 'NoSQL',
    icon: 'Database',
    versions: [
      { id: 'redis-5', label: 'Jedis 5.x', jarName: 'jedis-5.2.0.jar',
        downloadUrl: 'https://github.com/redis/jedis' },
    ],
    defaultVersion: 'redis-5',
    defaultPort: 6379,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Port', type: 'number', default: 6379, required: true },
      { key: 'database', label: 'Database Index', type: 'number', default: 0 },
      { key: 'password', label: 'Password', type: 'password', default: '' },
    ],
    advancedFields: [
      { key: 'ssl', label: 'Use SSL', type: 'checkbox', default: false },
      { key: 'cluster', label: 'Cluster Mode', type: 'checkbox', default: false },
    ],
    testQuery: null,
  },

  dynamodb: {
    id: 'dynamodb',
    name: 'DynamoDB',
    category: 'NoSQL',
    icon: 'Database',
    versions: [
      { id: 'ddb-2', label: 'AWS SDK v2', jarName: null, downloadUrl: null },
    ],
    defaultVersion: 'ddb-2',
    defaultPort: null,
    jdbcUrlTemplate: null,
    connectionFields: [
      { key: 'region', label: 'Region', type: 'select',
        options: ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'eu-west-1', 'eu-central-1'],
        default: 'us-east-1', required: true },
      { key: 'tableName', label: 'Table Name', type: 'text', default: '', required: true },
      { key: 'accessKeyId', label: 'Access Key ID', type: 'text', default: '' },
      { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password', default: '' },
    ],
    advancedFields: [
      { key: 'endpoint', label: 'Custom Endpoint (local)', type: 'text', default: '' },
    ],
    testQuery: null,
  },

  starrocks: {
    id: 'starrocks',
    name: 'StarRocks',
    category: 'Cloud',
    icon: 'Database',
    versions: [
      { id: 'sr-2', label: 'JDBC 2.x', jarName: 'starrocks-jdbc-2.0.jar',
        downloadUrl: 'https://docs.starrocks.io/docs/reference/jdbc/' },
    ],
    defaultVersion: 'sr-2',
    defaultPort: 9030,
    jdbcUrlTemplate: 'jdbc:mysql://{host}:{port}/{database}',
    connectionFields: [
      { key: 'host', label: 'FE Host', type: 'text', default: 'localhost', required: true },
      { key: 'port', label: 'Query Port', type: 'number', default: 9030, required: true },
      { key: 'database', label: 'Database', type: 'text', default: '', required: true },
      { key: 'username', label: 'User', type: 'text', default: 'root', required: true },
      { key: 'password', label: 'Password', type: 'password', default: '' },
    ],
    advancedFields: [],
    testQuery: 'SELECT 1',
  },
}

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

export function getDriver(driverId) {
  return DRIVER_REGISTRY[driverId] || null
}

export function getDriverVersion(driverId, versionId) {
  const driver = getDriver(driverId)
  if (!driver) return null
  return driver.versions.find(v => v.id === versionId) || null
}

export function listDriversByCategory() {
  const result = {}
  Object.values(DRIVER_REGISTRY).forEach(d => {
    if (!result[d.category]) result[d.category] = []
    result[d.category].push(d)
  })
  return result
}

export function getDefaultPort(driverId) {
  return DRIVER_REGISTRY[driverId]?.defaultPort || null
}

export function buildJdbcUrl(driverId, connectionValues) {
  const driver = getDriver(driverId)
  if (!driver?.jdbcUrlTemplate) return null
  let url = driver.jdbcUrlTemplate
  Object.entries(connectionValues).forEach(([key, value]) => {
    url = url.replace(`{${key}}`, value || '')
  })
  return url
}

/**
 * Determine the best driverId for a given ETL node type.
 * Maps node types like 'etl-source-sqlserver' to 'sqlserver'.
 * Generic 'etl-source-database' maps based on connection_type config.
 */
export function driverIdForNodeType(nodeType, config = {}) {
  const suffix = (nodeType || '').replace(/^etl-(source|target)-/, '')
  // Direct match
  if (DRIVER_REGISTRY[suffix]) return suffix
  // Generic database node — use connection_type from config
  if (suffix === 'database' && config.connection_type) {
    if (DRIVER_REGISTRY[config.connection_type]) return config.connection_type
  }
  return null
}

/**
 * List all driver IDs compatible with a given node type.
 * For specific nodes (etl-source-db2) returns just that driver.
 * For generic nodes (etl-source-database) returns all relational drivers.
 */
export function compatibleDrivers(nodeType) {
  const suffix = (nodeType || '').replace(/^etl-(source|target)-/, '')
  if (DRIVER_REGISTRY[suffix]) return [suffix]
  // Generic database node — all relational + cloud databases
  if (suffix === 'database') {
    return Object.values(DRIVER_REGISTRY)
      .filter(d => d.category === 'Relational' || d.category === 'Cloud')
      .map(d => d.id)
  }
  // Generic mappings
  const categoryMap = {
    'file': [],
    'cobol-copybook': [],
    'vsam': [],
    'pdf': [],
  }
  if (categoryMap[suffix]) return categoryMap[suffix]
  return []
}
